// src/components/booking/ConfirmationScreen.jsx
import { useNavigate } from "react-router-dom";
import { CheckCircle, Mail, Calendar, Clock, Scissors, MailCheck, MailX, Link, CreditCard } from "lucide-react";
import { ConfirmRow } from "../ui";
import { formatDate, bookingToUTC } from "../../utils/dateUtils";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../context/AuthContext";

function buildGoogleCalendarUrl({ service, barber, dateStr, time, tenantName, timezone }) {
  try {
    const tz       = timezone || "America/New_York";
    const startISO = bookingToUTC(dateStr, time, tz);
    const start    = new Date(startISO);
    const end      = new Date(start.getTime() + (service?.duration || 30) * 60 * 1000);

    const fmt = (d) =>
      d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

    const params = new URLSearchParams({
      action:   "TEMPLATE",
      text:     `${service?.name} at ${tenantName}`,
      dates:    `${fmt(start)}/${fmt(end)}`,
      details:  `Appointment with ${barber?.name}${service?.price ? ` · $${service.price}` : ""}`,
      location: tenantName,
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  } catch {
    return null;
  }
}

export default function ConfirmationScreen({ booking, bookingId, emailSent, onReset }) {
  const navigate   = useNavigate();
  const { tenant } = useTenant();
  const { user }   = useAuth();
  const { service, barber, date, time, customer, dateStr } = booking;
  const slug       = tenant?.slug || tenant?.id;
  const manageUrl  = bookingId && slug ? `/${slug}/booking/${bookingId}` : null;

  const gcalUrl = buildGoogleCalendarUrl({
    service,
    barber,
    dateStr: dateStr || (date ? date.toISOString().split("T")[0] : null),
    time,
    tenantName: tenant?.name,
    timezone:   tenant?.timezone,
  });

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-64
                        bg-brand-100 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="relative max-w-md w-full animate-slide-up">
        {/* Success icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-brand-100 border-4 border-brand-200
                          flex items-center justify-center">
            <CheckCircle size={40} className="text-brand-500" />
          </div>
        </div>

        <h1 className="font-semibold text-3xl text-center text-ink-900 mb-2">You're Booked!</h1>
        <p className="text-ink-400 text-sm text-center mb-2 font-sans">
          Your appointment at <strong className="text-ink-600">{tenant?.name}</strong> is confirmed.
        </p>

        {/* Email status pill */}
        <div className="flex justify-center mb-8">
          {emailSent ? (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50
                            border border-brand-200 text-brand-700 text-xs font-medium">
              <MailCheck size={14} /> Confirmation sent to {customer.email}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-100
                            border border-surface-300 text-ink-400 text-xs">
              <MailX size={14} /> Confirmation email could not be sent
            </div>
          )}
        </div>

        {/* Booking card */}
        <div className="card p-6 mb-5">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-surface-200">
            <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center">
              <Scissors size={18} className="text-brand-500" />
            </div>
            <div>
              <div className="font-semibold text-ink-900 text-lg leading-tight">{service?.name}</div>
              <div className="text-ink-400 text-xs font-mono mt-0.5">
                {service?.duration} min · ${service?.price}
              </div>
            </div>
          </div>

          <div>
            <ConfirmRow label="Member" value={barber?.name} />
            <ConfirmRow label="Date"   value={date ? formatDate(date) : ""} />
            <ConfirmRow label="Time"   value={time} />
            <ConfirmRow label="Name"   value={customer.name} />
            <ConfirmRow label="Total"  value={`$${service?.price}`} highlight />
          </div>
        </div>

        {/* Info pills */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { icon: Calendar, text: "Save the date" },
            { icon: Clock,    text: "Arrive 5 mins early" },
            { icon: Mail,     text: "Check your email" },
            tenant?.paymentMode === "online"
              ? { icon: CreditCard, text: "Paid online" }
              : { icon: Scissors,   text: "Payment at studio" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="card p-3 flex items-center gap-2.5">
              <Icon size={14} className="text-brand-500 shrink-0" />
              <span className="text-ink-500 text-xs font-sans">{text}</span>
            </div>
          ))}
        </div>

        {/* Guest manage link */}
        {!user && manageUrl && (
          <div className="card p-4 mb-4 flex items-start gap-3">
            <Link size={15} className="text-brand-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-ink-700 text-xs font-medium mb-1">Need to cancel or reschedule?</p>
              <button onClick={() => navigate(manageUrl)}
                className="text-brand-600 text-xs font-semibold hover:underline">
                Manage this booking →
              </button>
              <p className="text-ink-400 text-xs mt-1">
                Bookmark the link — it lets you manage this booking without an account.
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {gcalUrl && (
            <a
              href={gcalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <Calendar size={16} />
              Add to Google Calendar
            </a>
          )}
          <button onClick={onReset} className="btn-primary w-full">
            Book Another Appointment
          </button>
          <button onClick={() => navigate("/")} className="btn-secondary w-full">
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
